import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate, useSearchParams } from "react-router-dom";

import StatsCard from "../AdminDashboard/StatsCard";
import { useToast } from "../../hook/useToast";
import {
  fetchChannelPartnerCounsellor,
  fetchChannelPartnerCounsellorStudents,
  fetchChannelPartnerCounsellorWorkStats,
  fetchChannelPartnerSkillProvider,
  fetchChannelPartnerSkillProviderStudents,
  fetchChannelPartnerSkillProviderWorkStats,
  updateChannelPartnerCounsellorCompletion,
} from "../../services/channelPartnerPortalApi";
import ChannelPartnerDashboardLayout from "./ChannelPartnerDashboardLayout";
import { COUNSELLING_COMPLETION_EARNING_RUPEES } from "./earningRates";

const DEFAULT_PAGE_SIZE = 20;

const WORK_CONFIG = {
  counsellor: {
    queryKey: "counsellor",
    title: "Counsellor Work",
    entityLabel: "Counsellor",
    backPath: "/channel-partner-portal/counsellors",
    fetchDetail: fetchChannelPartnerCounsellor,
    fetchStats: fetchChannelPartnerCounsellorWorkStats,
    fetchStudents: fetchChannelPartnerCounsellorStudents,
  },
  skillProvider: {
    queryKey: "provider",
    title: "Skill Provider Work",
    entityLabel: "Skill Provider",
    backPath: "/channel-partner-portal/skill-providers",
    fetchDetail: fetchChannelPartnerSkillProvider,
    fetchStats: fetchChannelPartnerSkillProviderWorkStats,
    fetchStudents: fetchChannelPartnerSkillProviderStudents,
  },
};

const STATUS_COLORS = {
  pending: { bgcolor: "#FFF6E5", color: "#B54708" },
  completed: { bgcolor: "#EAF1FF", color: "#1F4FB6" },
  confirmed: { bgcolor: "#E6F4EA", color: "#1A7F37" },
  cancelled: { bgcolor: "#FDECEC", color: "#B42318" },
  failed: { bgcolor: "#F4F3FF", color: "#5925DC" },
};

const formatDateTime = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatStatus = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "--";
  return normalized
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const formatCurrency = (value) => new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
}).format(Number(value || 0));

const extractApiErrorMessage = (error, fallback) => {
  const responseData = error?.response?.data;
  if (typeof responseData?.message === "string" && responseData.message.trim()) {
    return responseData.message;
  }
  const errors = responseData?.errors || responseData?.data;
  if (errors && typeof errors === "object") {
    const firstMessage = Object.values(errors).flat().find(Boolean);
    if (typeof firstMessage === "string") return firstMessage;
  }
  return fallback;
};

const StatusChip = ({ value }) => {
  const normalized = String(value || "").toLowerCase();
  const colors = STATUS_COLORS[normalized] || {
    bgcolor: "#F1F2F4",
    color: "#475467",
  };
  return (
    <Chip
      label={formatStatus(value)}
      size="small"
      sx={{
        bgcolor: colors.bgcolor,
        color: colors.color,
        borderRadius: "6px",
        fontSize: 12,
        fontWeight: 600,
      }}
    />
  );
};

StatusChip.propTypes = {
  value: PropTypes.string,
};

StatusChip.defaultProps = {
  value: "",
};

const ChannelPartnerProfessionalWorkPage = ({ professionalType }) => {
  const config = WORK_CONFIG[professionalType];
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const professionalId = Number(searchParams.get(config.queryKey));
  const isCounsellor = professionalType === "counsellor";

  const [professional, setProfessional] = useState(null);
  const [stats, setStats] = useState({});
  const [students, setStudents] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    total_records: 0,
    total_pages: 0,
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatingAppointmentId, setUpdatingAppointmentId] = useState(null);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    if (!professionalId) {
      setSummaryLoading(false);
      setError(`${config.entityLabel} was not selected.`);
      return;
    }

    let active = true;
    setSummaryLoading(true);
    setError("");
    Promise.all([
      config.fetchDetail(professionalId),
      config.fetchStats(professionalId),
    ])
      .then(([detailResponse, statsResponse]) => {
        if (!active) return;
        setProfessional(detailResponse?.data?.data || null);
        setStats(statsResponse?.data?.data || {});
      })
      .catch((requestError) => {
        if (!active) return;
        setError(extractApiErrorMessage(
          requestError,
          `Unable to load ${config.entityLabel.toLowerCase()} work details.`,
        ));
      })
      .finally(() => {
        if (active) setSummaryLoading(false);
      });

    return () => {
      active = false;
    };
  }, [config, professionalId]);

  const loadStudents = useCallback(async () => {
    if (!professionalId) {
      setStudents([]);
      setStudentsLoading(false);
      return;
    }
    setStudentsLoading(true);
    try {
      const response = await config.fetchStudents(professionalId, {
        page,
        pageSize,
        search: debouncedSearch,
      });
      const data = response?.data?.data || {};
      setStudents(Array.isArray(data.students) ? data.students : []);
      setPagination({
        page: data.pagination?.page || page,
        page_size: data.pagination?.page_size || pageSize,
        total_records: data.pagination?.total_records || 0,
        total_pages: data.pagination?.total_pages || 0,
      });
    } catch (requestError) {
      setStudents([]);
      showToast(
        extractApiErrorMessage(requestError, "Failed to load student sessions."),
        "error",
      );
    } finally {
      setStudentsLoading(false);
    }
  }, [
    config,
    debouncedSearch,
    page,
    pageSize,
    professionalId,
    showToast,
  ]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const columns = useMemo(() => {
    const commonColumns = [
      { id: "student_name", label: "Student Name", width: 180 },
      { id: "email", label: "Email", width: 220 },
      { id: "school", label: "School", width: 180 },
      { id: "board", label: "Board", width: 110 },
      { id: "class_name", label: "Class", width: 80 },
      { id: "city", label: "City", width: 150 },
    ];
    if (isCounsellor) {
      return [
        ...commonColumns,
        { id: "start_at", label: "Counselling Date", width: 180 },
        { id: "booking_status", label: "Booking Status", width: 125 },
        { id: "completion_status", label: "Completion Status", width: 170 },
        { id: "earning_amount", label: "Earning", width: 105 },
        { id: "total_sessions", label: "Total Sessions", width: 120 },
      ];
    }
    return [
      ...commonColumns,
      { id: "skill", label: "Skill", width: 150 },
      { id: "start_at", label: "Session Date", width: 180 },
      { id: "booking_status", label: "Booking Status", width: 125 },
      { id: "total_sessions", label: "Total Sessions", width: 120 },
    ];
  }, [isCounsellor]);

  const handleCompletionChange = async (studentRow, completionStatus) => {
    setUpdatingAppointmentId(studentRow.appointment_id);
    try {
      const response = await updateChannelPartnerCounsellorCompletion(
        professionalId,
        studentRow.appointment_id,
        completionStatus,
      );
      const updatedStatus = (
        response?.data?.data?.completion_status || completionStatus
      );
      const updatedEarning = response?.data?.data?.earning_amount || 0;
      setStudents((currentRows) => currentRows.map((row) => (
        row.appointment_id === studentRow.appointment_id
          ? {
            ...row,
            completion_status: updatedStatus,
            earning_amount: updatedEarning,
          }
          : row
      )));
      try {
        const statsResponse = await config.fetchStats(professionalId);
        setStats(statsResponse?.data?.data || {});
      } catch {
        // The completion is saved; a later refresh will reload its total.
      }
      showToast("Counselling completion status updated", "success");
    } catch (requestError) {
      showToast(
        extractApiErrorMessage(requestError, "Failed to update completion status."),
        "error",
      );
    } finally {
      setUpdatingAppointmentId(null);
    }
  };

  const renderCell = (row, columnId) => {
    if (columnId === "start_at") return formatDateTime(row.start_at);
    if (columnId === "booking_status") {
      return <StatusChip value={row.booking_status} />;
    }
    if (columnId === "completion_status") {
      return (
        <FormControl size="small" sx={{ minWidth: 135 }}>
          <Select
            value={row.completion_status || "pending"}
            disabled={updatingAppointmentId === row.appointment_id}
            onChange={(event) => handleCompletionChange(row, event.target.value)}
            sx={{
              height: 34,
              borderRadius: "7px",
              fontSize: 12,
              fontWeight: 600,
              color: row.completion_status === "completed"
                ? "#1F4FB6"
                : "#B54708",
            }}
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
          </Select>
        </FormControl>
      );
    }
    if (columnId === "earning_amount") {
      const amount = Number(row.earning_amount || 0);
      const isEligiblePending = (
        row.completion_status !== "completed"
        && !["cancelled", "failed"].includes(row.booking_status)
      );
      return (
        <Typography
          component="span"
          sx={{
            color: amount > 0 ? "#027A48" : isEligiblePending ? "#B54708" : "#92929D",
            fontSize: 13,
            fontWeight: 750,
            whiteSpace: "nowrap",
          }}
        >
          {amount > 0
            ? `+${formatCurrency(amount)}`
            : isEligiblePending
              ? `${formatCurrency(
                stats.completion_earning_rate
                  || COUNSELLING_COMPLETION_EARNING_RUPEES,
              )} Pending`
              : "Not eligible"}
        </Typography>
      );
    }
    return row[columnId] ?? "--";
  };

  return (
    <ChannelPartnerDashboardLayout>
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(config.backPath)}
          sx={{ color: "#292E5C", textTransform: "none", mb: 1 }}
        >
          Back to {config.entityLabel}s
        </Button>

        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#171725" }}>
            {config.title}
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: "#667085" }}>
            {professional?.name || professional?.full_name || config.entityLabel}
            {professional?.email ? ` · ${professional.email}` : ""}
          </Typography>
        </Box>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              xl: `repeat(${isCounsellor ? 5 : 4}, 1fr)`,
            },
            gap: 2,
            mb: 2.2,
          }}
        >
          <StatsCard
            value={summaryLoading ? "--" : stats.total_students || 0}
            label="Unique Students"
            icon="total"
            bgColor="#F5F6FA"
          />
          <StatsCard
            value={summaryLoading ? "--" : stats.total_sessions || 0}
            label="Total Sessions"
            icon="total"
            bgColor="#F2F6FF"
          />
          <StatsCard
            value={summaryLoading ? "--" : stats.sessions_this_month || 0}
            label="Sessions This Month"
            icon="month"
            bgColor="#FFF9F1"
          />
          <StatsCard
            value={summaryLoading ? "--" : stats.sessions_this_year || 0}
            label="Sessions This Year"
            icon="year"
            bgColor="#F4F8F3"
          />
          {isCounsellor && (
            <StatsCard
              value={summaryLoading ? "--" : formatCurrency(stats.total_earnings)}
              label="Counselling Earnings"
              icon="total"
              bgColor="#ECFDF3"
            />
          )}
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 1.25,
            bgcolor: "rgba(239, 239, 239, 0.40)",
            borderRadius: "8px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.5,
              mb: 1.25,
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#171725" }}>
              Student Sessions
            </Typography>
            <TextField
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search students..."
              size="small"
              sx={{
                width: { xs: "100%", sm: 280 },
                "& .MuiOutlinedInput-root": {
                  height: 38,
                  bgcolor: "#fff",
                  borderRadius: "7px",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ fontSize: 18, color: "#667085" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <TableContainer
            sx={{
              minHeight: 330,
              bgcolor: "#fff",
              border: "1px solid #EAECF0",
              borderRadius: "7px",
            }}
          >
            <Table sx={{ minWidth: isCounsellor ? 1680 : 1510 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "#F9FAFB" }}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      sx={{
                        minWidth: column.width,
                        width: column.width,
                        color: "#667085",
                        fontSize: 12,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {studentsLoading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" sx={{ py: 8 }}>
                      <CircularProgress size={28} sx={{ color: "#292E5C" }} />
                    </TableCell>
                  </TableRow>
                ) : students.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      align="center"
                      sx={{ py: 8, color: "#667085", fontSize: 14 }}
                    >
                      No student sessions found.
                    </TableCell>
                  </TableRow>
                ) : students.map((studentRow) => (
                  <TableRow
                    key={studentRow.appointment_id}
                    hover
                    sx={{ "& td": { borderBottom: "1px solid #EAECF0" } }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        sx={{
                          py: 1.5,
                          color: "#344054",
                          fontSize: 13,
                          maxWidth: column.width,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {renderCell(studentRow, column.id)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {isCounsellor && (
            <Box
              sx={{
                px: 2,
                pt: 1.4,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Typography sx={{ fontSize: 13, color: "#027A48", fontWeight: 750 }}>
                Total counselling earnings: {formatCurrency(stats.total_earnings)}
              </Typography>
            </Box>
          )}

          <TablePagination
            component="div"
            count={pagination.total_records || 0}
            page={Math.max(0, page - 1)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[10, 20, 50, 100]}
            onPageChange={(_, nextPage) => setPage(nextPage + 1)}
            onRowsPerPageChange={(event) => {
              setPageSize(Number(event.target.value));
              setPage(1);
            }}
          />
        </Paper>
      </Box>
    </ChannelPartnerDashboardLayout>
  );
};

ChannelPartnerProfessionalWorkPage.propTypes = {
  professionalType: PropTypes.oneOf(["counsellor", "skillProvider"]).isRequired,
};

export default ChannelPartnerProfessionalWorkPage;
